import { type ComputedRef, computed, type ShallowRef, shallowRef } from 'vue';
import { router } from '@/app/router';
import { store } from '@/app/store';
import { redirectToPrivacyConsentIfNeeded } from '@/components/steps/gdpr-consent/privacyConsent';
import { analytics } from '@/services/Analytics';
import { animations } from '@/services/Animations';
import { restApi } from '@/services/api';
import { faceDetection } from '@/services/FaceDetection';
import { kioskHardware } from '@/services/KioskHardware';
import { photoProcessing } from '@/services/PhotoProcessing';
import { session } from '@/services/Session';
import { kioskErrorProperties } from '@/services/telemetry/kiosk-error';
import { kioskJourney } from '@/services/telemetry/kiosk-journey';
import { sentryService } from '@/services/telemetry/sentry';
import { describeError, track } from '@/shared/utils/track-telemetry';

export const WEBCAM_CAPTURE_STATES = {
  PHOTO_NOT_TAKEN: 'PHOTO_NOT_TAKEN',
  PHOTO_TAKEN_1: 'PHOTO_TAKEN_1',
  PHOTO_RETAKING: 'PHOTO_RETAKING',
  PHOTO_TAKEN_2: 'PHOTO_TAKEN_2',
} as const;

export type WebcamCaptureState =
  (typeof WEBCAM_CAPTURE_STATES)[keyof typeof WEBCAM_CAPTURE_STATES];
export type WebcamPhotoIndex = 0 | 1;

export interface WebcamTargets {
  container: HTMLElement;
  video: HTMLVideoElement;
}

export interface WebcamCaptureControlsHandle {
  buzzUploadFromPhone: () => void;
  pulseCountdown: (duration: number) => void;
  pulseInitialActions: () => void;
  pulseRetakeTake: () => void;
  pulseUsePhoto: (photoIndex: WebcamPhotoIndex) => void;
}

interface WebcamPhoto {
  blob?: Blob;
  url?: string;
  uuid?: string;
}

interface ExistingSessionPhoto extends Record<string, unknown> {
  source?: string;
  uuid: string;
}

interface WebcamDeviceContext {
  browser: unknown;
  enumerateError?: string;
  hasGetUserMedia: boolean;
  hasMediaDevices: boolean;
  isSecureContext: boolean;
  platform: string;
  protocol: string;
  videoDeviceCount?: number;
  videoDeviceLabels?: string[];
}

export interface UseWebcamCaptureFlowOptions {
  getControls: () => WebcamCaptureControlsHandle | null;
  getFlashTarget: () => HTMLElement | object;
  getWebcamTargets: () => WebcamTargets;
}

export interface WebcamCaptureFlow {
  countdown: ShallowRef<number | null>;
  currentState: ShallowRef<WebcamCaptureState>;
  enter: () => Promise<void>;
  isPhotographingInProgress: ShallowRef<boolean>;
  isShowingTakenPhoto: ComputedRef<boolean>;
  isTakenPhotoLoaded: ShallowRef<boolean>;
  isUploadingInProgress: ShallowRef<boolean>;
  isWebcamReady: ShallowRef<boolean>;
  lastTakenPhoto: ComputedRef<WebcamPhoto | undefined>;
  launchWebcamCountdown: (
    stepsTotal: number,
    stepsLeft?: number | null
  ) => Promise<void>;
  leave: () => void;
  onPhotoUploadAndAnimationComplete: () => void;
  onTakenPhotoLoad: () => void;
  retryPhoto: () => void;
  retryWebcamInit: () => void;
  showUploadFromPhoneButton: ComputedRef<boolean>;
  takePhoto: (photoIndex: WebcamPhotoIndex) => void;
  usePhoto: (
    photoIndex: WebcamPhotoIndex,
    animateFromRef: HTMLElement
  ) => Promise<void>;
  webcamInitFailed: ShallowRef<boolean>;
}

function warn(context: string, error?: unknown): void {
  // biome-ignore lint/suspicious/noConsole: preserve operational camera diagnostics.
  console.warn(context, error === undefined ? '' : describeError(error));
}

function reportError(context: string, error: unknown, extra?: unknown): void {
  // biome-ignore lint/suspicious/noConsole: preserve operational camera diagnostics.
  console.error(context, error, extra);
}

async function runWithSpanFallback(
  name: string,
  op: string,
  action: () => Promise<void>,
  attrs: Record<string, unknown>,
  label: string
): Promise<void> {
  try {
    await sentryService.withSpan(name, op, action, attrs);
  } catch (error) {
    if (describeError(error).includes('Sentry')) {
      warn(label, error);
      await action();
    } else {
      throw error;
    }
  }
}

export function useWebcamCaptureFlow({
  getControls,
  getFlashTarget,
  getWebcamTargets,
}: UseWebcamCaptureFlowOptions): WebcamCaptureFlow {
  const currentState = shallowRef<WebcamCaptureState>(
    WEBCAM_CAPTURE_STATES.PHOTO_NOT_TAKEN
  );
  const isWebcamReady = shallowRef(false);
  const webcamInitFailed = shallowRef(false);
  const isPhotographingInProgress = shallowRef(false);
  const isUploadingInProgress = shallowRef(false);
  const countdown = shallowRef<number | null>(null);
  const isTakenPhotoLoaded = shallowRef(false);
  const isShowingTakenPhoto = computed(
    (): boolean =>
      currentState.value === WEBCAM_CAPTURE_STATES.PHOTO_TAKEN_1 ||
      currentState.value === WEBCAM_CAPTURE_STATES.PHOTO_TAKEN_2
  );
  const lastTakenPhoto = computed((): WebcamPhoto | undefined => {
    const photos = faceDetection.queryPhotos({ webcam: true }) as WebcamPhoto[];
    return photos.at(-1);
  });
  const showUploadFromPhoneButton = computed((): boolean => {
    const hasUploadedPhonePhotos =
      faceDetection.queryPhotos({ mobile: true, uploaded: true }).length > 0;
    return (
      router.query?.from !== 'phone' &&
      !store.photosHasSkippedMobileUploads &&
      !hasUploadedPhonePhotos &&
      (store.kiosk as { hide_mobile_upload?: boolean } | null)
        ?.hide_mobile_upload !== true
    );
  });

  const analyticsVisitId = kioskJourney.id;
  let disposed = false;
  let sessionEndHandler: (() => void) | null = null;
  let countdownTimeoutId: ReturnType<typeof setTimeout> | null = null;
  let takePhotoTimeoutId: ReturnType<typeof setTimeout> | null = null;
  let uploadAnimationTimeoutId: ReturnType<typeof setTimeout> | null = null;
  let uploadSafetyTimeoutId: ReturnType<typeof setTimeout> | null = null;
  let photoCaptureStartTime = 0;

  function trackMounted(): void {
    track(
      '[Sentry] Tracking failed:',
      () => {
        sentryService.addBreadcrumb('navigation', 'PhotoFromWebcam mounted', {
          hasGdprConsent: session.gdprConsent,
          kioskSlug: store.kiosk?.slug,
        });
      },
      warn
    );
  }

  function subscribeToSessionEnd(): void {
    sessionEndHandler = (): void => {
      photoProcessing.stopWebcam();
    };
    session.on(session.EVENTS.ENDED, sessionEndHandler);
  }

  async function hydrateExistingSessionPhotos(): Promise<void> {
    try {
      const existingPhotos =
        (await restApi.getUploadedPhotosInSession()) as ExistingSessionPhoto[];
      if (existingPhotos.length > 0) {
        track(
          '[Sentry] Tracking failed:',
          () => {
            sentryService.addBreadcrumb(
              'data',
              'Found existing session photos',
              {
                count: existingPhotos.length,
              }
            );
          },
          warn
        );
        for (const photo of existingPhotos) {
          if (faceDetection.getPhotoByUuid(photo.uuid)) {
            continue;
          }
          const photoData = { ...photo, isUploaded: true };
          if (photo.source === 'mobile') {
            faceDetection.addPhotosFromMobile([photoData]);
          } else if (photo.source === 'webcam') {
            faceDetection.addPhotosFromWebcam([photoData]);
          }
        }
        faceDetection.updateNextStep();
      }
    } catch (error) {
      reportError('[PhotoFromWebcam] Error fetching existing photos:', error);
      track(
        '[Sentry] Tracking failed:',
        () => {
          sentryService.captureException(error, {
            tags: { operation: 'fetch_existing_photos' },
            extra: { component: 'PhotoFromWebcam' },
          });
        },
        warn
      );
    }
  }

  async function initializeWebcamDevice(): Promise<void> {
    const webcamInitStartTime = performance.now();
    try {
      const performWebcamInit = async (): Promise<void> => {
        const targets = getWebcamTargets();
        await photoProcessing.initializeWebcam(
          targets.container,
          targets.video,
          1.2
        );
      };

      await runWithSpanFallback(
        'webcam.initialization',
        'device.webcam',
        performWebcamInit,
        {
          'webcam.zoom': 1.2,
          'kiosk.slug': store.kiosk?.slug,
        },
        '[PhotoFromWebcam] Sentry withSpan failed, initializing webcam directly:'
      );

      if (disposed) {
        return;
      }

      const initDuration = performance.now() - webcamInitStartTime;
      isWebcamReady.value = true;
      track(
        '[PhotoFromWebcam] Sentry call failed:',
        () => {
          sentryService.addBreadcrumb(
            'device',
            'Webcam initialized successfully',
            { duration_ms: Math.round(initDuration) }
          );
          sentryService.trackMetric(
            'webcam.init_duration',
            initDuration,
            'millisecond',
            { success: 'true' }
          );
        },
        warn
      );

      getControls()?.pulseInitialActions();
      getControls()?.buzzUploadFromPhone();
    } catch (error) {
      const initDuration = performance.now() - webcamInitStartTime;
      if (!disposed) {
        kioskJourney.record(
          'photo_failed',
          { source: 'webcam', ...kioskErrorProperties(error, 'camera') },
          analyticsVisitId
        );
      }
      const deviceContext: WebcamDeviceContext = {
        browser: (
          kioskHardware as unknown as {
            _getBrowserFamily: (userAgent: string) => unknown;
          }
        )._getBrowserFamily(navigator.userAgent),
        platform: navigator.platform,
        hasMediaDevices: Boolean(navigator.mediaDevices),
        hasGetUserMedia: Boolean(navigator.mediaDevices?.getUserMedia),
        isSecureContext: window.isSecureContext,
        protocol: window.location.protocol,
      };

      try {
        if (navigator.mediaDevices?.enumerateDevices) {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const videoDevices = devices.filter(
            (device) => device.kind === 'videoinput'
          );
          deviceContext.videoDeviceCount = videoDevices.length;
          deviceContext.videoDeviceLabels = videoDevices.map(
            (device) => device.label || 'unnamed'
          );
        }
      } catch (enumerationError) {
        deviceContext.enumerateError = describeError(enumerationError);
      }

      track(
        '[PhotoFromWebcam] Sentry call failed:',
        () => {
          sentryService.addBreadcrumb(
            'device',
            'Webcam initialization failed',
            {
              duration_ms: Math.round(initDuration),
              error_message: describeError(error),
            },
            'error'
          );
          sentryService.trackMetric(
            'webcam.init_duration',
            initDuration,
            'millisecond',
            { success: 'false' }
          );
          sentryService.captureException(error, {
            tags: {
              operation: 'webcam_initialization',
              component: 'PhotoFromWebcam',
            },
            extra: {
              deviceContext,
              initDuration: Math.round(initDuration),
              webcamZoom: 1.2,
            },
            level: 'error',
          });
        },
        warn
      );

      reportError(
        '[PhotoFromWebcam] Webcam initialization failed:',
        error,
        deviceContext
      );
      webcamInitFailed.value = true;
    }
  }

  async function enter(): Promise<void> {
    if (!store.sessionId) {
      router.redirectToStep('');
      return;
    }
    trackMounted();
    subscribeToSessionEnd();
    if (
      redirectToPrivacyConsentIfNeeded(
        'photo-from-webcam',
        { nextStep: 'photo-from-webcam' },
        warn
      )
    ) {
      return;
    }

    analytics.trackTimeTo(analytics.METRICS.time_photo_initiated, {});
    await hydrateExistingSessionPhotos();
    if (disposed) {
      return;
    }
    await initializeWebcamDevice();
  }

  function leave(): void {
    if (disposed) {
      return;
    }
    disposed = true;

    track(
      '[PhotoFromWebcam] Sentry call failed:',
      () => {
        sentryService.addBreadcrumb(
          'navigation',
          'PhotoFromWebcam unmounting',
          {
            wasWebcamReady: isWebcamReady.value,
            finalState: currentState.value,
          }
        );
      },
      warn
    );

    if (sessionEndHandler) {
      session.off(session.EVENTS.ENDED, sessionEndHandler);
      sessionEndHandler = null;
    }
    photoProcessing.stopWebcam();

    if (countdownTimeoutId) {
      clearTimeout(countdownTimeoutId);
      countdownTimeoutId = null;
    }
    if (takePhotoTimeoutId) {
      clearTimeout(takePhotoTimeoutId);
      takePhotoTimeoutId = null;
    }
    if (uploadAnimationTimeoutId) {
      clearTimeout(uploadAnimationTimeoutId);
      uploadAnimationTimeoutId = null;
    }
    if (uploadSafetyTimeoutId) {
      clearTimeout(uploadSafetyTimeoutId);
      uploadSafetyTimeoutId = null;
    }
    animations.stopAllAnimations();
  }

  function retryWebcamInit(): void {
    track(
      '[PhotoFromWebcam] Sentry call failed:',
      () => {
        sentryService.addBreadcrumb(
          'user',
          'User retrying webcam initialization'
        );
        sentryService.incrementCounter('webcam.retry_init', 1);
      },
      warn
    );
    router.redirectToStep('photo-from-webcam');
  }

  function takePhoto(photoIndex: WebcamPhotoIndex): void {
    if (isPhotographingInProgress.value) {
      return;
    }
    if (takePhotoTimeoutId) {
      clearTimeout(takePhotoTimeoutId);
      takePhotoTimeoutId = null;
    }

    isPhotographingInProgress.value = true;
    photoCaptureStartTime = performance.now();
    track(
      '[PhotoFromWebcam] Sentry call failed:',
      () => {
        sentryService.addBreadcrumb('user', 'Photo capture initiated', {
          photoIndex,
          isRetake: photoIndex > 0,
        });
        sentryService.incrementCounter('webcam.photo_capture_started', 1, {
          photo_index: String(photoIndex),
        });
      },
      warn
    );

    takePhotoTimeoutId = setTimeout(async (): Promise<void> => {
      if (disposed) {
        return;
      }

      try {
        await launchWebcamCountdown(3);
        if (disposed) {
          return;
        }

        animations.flash(getFlashTarget());
        const blob = await photoProcessing.getPreparedBlobFromWebcam();
        if (disposed) {
          return;
        }

        const captureDuration = performance.now() - photoCaptureStartTime;
        track(
          '[PhotoFromWebcam] Sentry call failed:',
          () => {
            sentryService.trackMetric(
              'webcam.photo_capture_duration',
              captureDuration,
              'millisecond',
              { photo_index: String(photoIndex) }
            );
            sentryService.addBreadcrumb('user', 'Photo captured from webcam', {
              photoIndex,
              duration_ms: Math.round(captureDuration),
              blobSize: blob?.size || 0,
            });
          },
          warn
        );

        faceDetection.addPhotosFromWebcam(blob);
        currentState.value =
          photoIndex === 0
            ? WEBCAM_CAPTURE_STATES.PHOTO_TAKEN_1
            : WEBCAM_CAPTURE_STATES.PHOTO_TAKEN_2;
        getControls()?.pulseUsePhoto(photoIndex);
      } catch (error) {
        if (disposed) {
          return;
        }
        const captureDuration = performance.now() - photoCaptureStartTime;
        kioskJourney.record(
          'photo_failed',
          { source: 'webcam', ...kioskErrorProperties(error, 'camera') },
          analyticsVisitId
        );
        track(
          '[PhotoFromWebcam] Sentry call failed:',
          () => {
            sentryService.addBreadcrumb(
              'device',
              'Photo capture failed',
              {
                photoIndex,
                duration_ms: Math.round(captureDuration),
                error_message: describeError(error),
              },
              'error'
            );
            sentryService.captureException(error, {
              tags: {
                operation: 'photo_capture',
                component: 'PhotoFromWebcam',
              },
              extra: {
                photoIndex,
                captureDuration: Math.round(captureDuration),
              },
            });
          },
          warn
        );
        reportError('[PhotoFromWebcam] Photo capture failed:', error);
      } finally {
        if (!disposed) {
          isPhotographingInProgress.value = false;
        }
      }
    }, 300);
  }

  async function usePhoto(
    photoIndex: WebcamPhotoIndex,
    animateFromRef: HTMLElement
  ): Promise<void> {
    if (!store.sessionId) {
      router.redirectToStep('');
      return;
    }

    isUploadingInProgress.value = true;
    const webcamPhotoByIndex = faceDetection.queryPhotos({
      webcam: true,
      uploaded: false,
      index: photoIndex,
    }) as WebcamPhoto | undefined;

    track(
      '[PhotoFromWebcam] Sentry call failed:',
      () => {
        sentryService.addBreadcrumb('user', 'Photo upload initiated', {
          photoIndex,
          photoUuid: webcamPhotoByIndex?.uuid,
        });
      },
      warn
    );

    const uploadStartTime = performance.now();
    try {
      const uploadPhoto = async (): Promise<void> => {
        await faceDetection.upload(webcamPhotoByIndex, animateFromRef);
      };
      await runWithSpanFallback(
        'webcam.photo_upload',
        'http.upload',
        uploadPhoto,
        {
          'photo.index': photoIndex,
          'photo.uuid': webcamPhotoByIndex?.uuid,
          'photo.source': 'webcam',
        },
        '[PhotoFromWebcam] Sentry withSpan failed, running upload directly:'
      );

      const uploadDuration = performance.now() - uploadStartTime;
      track(
        '[PhotoFromWebcam] Sentry call failed:',
        () => {
          sentryService.trackMetric(
            'webcam.photo_upload_duration',
            uploadDuration,
            'millisecond',
            { success: 'true', photo_index: String(photoIndex) }
          );
          sentryService.addBreadcrumb('http', 'Photo upload completed', {
            photoIndex,
            duration_ms: Math.round(uploadDuration),
          });
          sentryService.incrementCounter('webcam.photo_upload_success', 1);
        },
        warn
      );

      uploadSafetyTimeoutId = setTimeout((): void => {
        uploadSafetyTimeoutId = null;
        if (disposed || !isUploadingInProgress.value) {
          return;
        }
        warn('[PhotoFromWebcam] Upload animation timeout, forcing navigation');
        try {
          sentryService.addBreadcrumb(
            'ui',
            'Upload animation safety timeout triggered',
            { nextStep: store.photoUploadNextStep },
            'warning'
          );
        } catch (_error) {
          // Navigation is the recovery path even when telemetry is unavailable.
        }
        isUploadingInProgress.value = false;
        router.redirectToStep(store.photoUploadNextStep as unknown as string, {
          omitQuery: true,
        });
      }, 10_000);
    } catch (error) {
      const uploadDuration = performance.now() - uploadStartTime;
      track(
        '[PhotoFromWebcam] Sentry call failed:',
        () => {
          sentryService.trackMetric(
            'webcam.photo_upload_duration',
            uploadDuration,
            'millisecond',
            { success: 'false', photo_index: String(photoIndex) }
          );
          sentryService.addBreadcrumb(
            'http',
            'Photo upload failed',
            {
              photoIndex,
              duration_ms: Math.round(uploadDuration),
              error_message: describeError(error),
            },
            'error'
          );
          sentryService.incrementCounter('webcam.photo_upload_failure', 1);
        },
        warn
      );

      isUploadingInProgress.value = false;
      currentState.value = WEBCAM_CAPTURE_STATES.PHOTO_RETAKING;
      if (uploadSafetyTimeoutId) {
        clearTimeout(uploadSafetyTimeoutId);
        uploadSafetyTimeoutId = null;
      }
    }
  }

  function onPhotoUploadAndAnimationComplete(): void {
    track(
      '[PhotoFromWebcam] Sentry call failed:',
      () => {
        sentryService.addBreadcrumb('ui', 'Photo upload animation complete', {
          nextStep: store.photoUploadNextStep,
        });
      },
      warn
    );

    if (uploadSafetyTimeoutId) {
      clearTimeout(uploadSafetyTimeoutId);
      uploadSafetyTimeoutId = null;
    }
    if (uploadAnimationTimeoutId) {
      clearTimeout(uploadAnimationTimeoutId);
    }
    uploadAnimationTimeoutId = setTimeout((): void => {
      uploadAnimationTimeoutId = null;
      if (disposed) {
        return;
      }
      isUploadingInProgress.value = false;
      router.redirectToStep(store.photoUploadNextStep as unknown as string, {
        omitQuery: true,
      });
    }, 1000);
  }

  function onTakenPhotoLoad(): void {
    isTakenPhotoLoaded.value = true;
  }

  function retryPhoto(): void {
    if (countdownTimeoutId) {
      clearTimeout(countdownTimeoutId);
      countdownTimeoutId = null;
    }
    countdown.value = null;
    isPhotographingInProgress.value = false;
    isTakenPhotoLoaded.value = false;

    track(
      '[PhotoFromWebcam] Sentry call failed:',
      () => {
        sentryService.addBreadcrumb('user', 'User initiated photo retake', {
          previousState: currentState.value,
        });
        sentryService.incrementCounter('webcam.photo_retake_requested', 1);
      },
      warn
    );

    currentState.value = WEBCAM_CAPTURE_STATES.PHOTO_RETAKING;
    getControls()?.pulseRetakeTake();
  }

  function launchWebcamCountdown(
    stepsTotal: number,
    stepsLeft?: number | null
  ): Promise<void> {
    if (countdownTimeoutId) {
      clearTimeout(countdownTimeoutId);
      countdownTimeoutId = null;
    }
    if (disposed) {
      return Promise.resolve();
    }

    const duration = store.isDevMode ? 500 : 1000;
    getControls()?.pulseCountdown(duration * 0.8);
    countdown.value =
      stepsLeft === undefined ? stepsTotal : (stepsLeft ?? 0) - 1;

    return new Promise((resolve) => {
      if (countdown.value === 0) {
        countdown.value = null;
        countdownTimeoutId = null;
        resolve();
        return;
      }
      countdownTimeoutId = setTimeout((): void => {
        if (disposed) {
          resolve();
          return;
        }
        void launchWebcamCountdown(stepsTotal, countdown.value).then(resolve);
      }, duration);
    });
  }

  return {
    countdown,
    currentState,
    enter,
    isPhotographingInProgress,
    isShowingTakenPhoto,
    isTakenPhotoLoaded,
    isUploadingInProgress,
    isWebcamReady,
    lastTakenPhoto,
    launchWebcamCountdown,
    leave,
    onPhotoUploadAndAnimationComplete,
    onTakenPhotoLoad,
    retryPhoto,
    retryWebcamInit,
    showUploadFromPhoneButton,
    takePhoto,
    usePhoto,
    webcamInitFailed,
  };
}
